<script setup lang="ts">
import { Link } from '@inertiajs/vue3';
import { Hash, Loader2, Search, X } from 'lucide-vue-next';
import { computed, ref, watch } from 'vue';
import type { HelpArticleSummary, HelpIndex, HelpSearchResult } from '@/types/help';

interface Props {
    index: HelpIndex;
    currentPath?: string | null;
}

const props = defineProps<Props>();

const query = ref('');
const searching = ref(false);
const searchResults = ref<HelpSearchResult[] | null>(null);
const searchError = ref<string | null>(null);

let debounceHandle: ReturnType<typeof setTimeout> | null = null;
let latestRequestId = 0;

const clearQuery = () => {
    query.value = '';
    searchResults.value = null;
    searchError.value = null;
};

const normalised = (value: string) => value.toLowerCase();

const matches = (article: HelpArticleSummary, q: string): boolean => {
    if (!q) {
        return true;
    }

    return normalised(article.title).includes(q) || normalised(article.description).includes(q);
};

// Fast client-side filter used while the semantic request is in flight,
// and as the only view when the query is too short to hit the server.
const filteredTopLevel = computed(() => {
    const q = normalised(query.value.trim());

    return props.index.topLevel.filter((a) => matches(a, q));
});

const filteredSections = computed(() => {
    const q = normalised(query.value.trim());

    return {
        agent: props.index.sections.agent.filter((a) => matches(a, q)),
        supplier: props.index.sections.supplier.filter((a) => matches(a, q)),
        shared: props.index.sections.shared.filter((a) => matches(a, q)),
    };
});

const hasActiveQuery = computed(() => query.value.trim().length >= 2);

const showSemanticResults = computed(() => hasActiveQuery.value && searchResults.value !== null && searchResults.value.length > 0);

const showSearchingPlaceholder = computed(() => hasActiveQuery.value && searching.value && searchResults.value === null);

const isActive = (path: string): boolean => props.currentPath === path;

const sectionLabels: Record<'agent' | 'supplier' | 'shared', string> = {
    agent: 'For agents',
    supplier: 'For suppliers',
    shared: 'Everyone',
};

const sectionOrder: Array<'agent' | 'supplier' | 'shared'> = ['shared', 'agent', 'supplier'];

const runSemanticSearch = async (q: string) => {
    const requestId = ++latestRequestId;

    try {
        searching.value = true;
        searchError.value = null;
        const url = `/help/search?q=${encodeURIComponent(q)}`;
        const response = await fetch(url, {
            headers: { Accept: 'application/json' },
        });

        if (!response.ok) {
            throw new Error(`Search failed (${response.status})`);
        }

        const payload = (await response.json()) as { results: HelpSearchResult[] };

        // Discard stale responses if the user has kept typing.
        if (requestId !== latestRequestId) {
            return;
        }

        searchResults.value = payload.results;
    } catch (err) {
        if (requestId !== latestRequestId) {
            return;
        }

        searchError.value = err instanceof Error ? err.message : 'Search failed';
        searchResults.value = null;
    } finally {
        if (requestId === latestRequestId) {
            searching.value = false;
        }
    }
};

watch(query, (value) => {
    if (debounceHandle) {
        clearTimeout(debounceHandle);
        debounceHandle = null;
    }

    const trimmed = value.trim();

    if (trimmed.length < 2) {
        searchResults.value = null;
        searchError.value = null;
        searching.value = false;
        latestRequestId++;

        return;
    }

    debounceHandle = setTimeout(() => {
        void runSemanticSearch(trimmed);
    }, 250);
});
</script>

<template>
    <aside class="flex flex-col gap-6">
        <div class="relative">
            <Search class="text-muted-foreground pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
            <input
                v-model="query"
                type="search"
                placeholder="Search help articles..."
                class="border-border bg-card text-foreground placeholder:text-muted-foreground focus:border-primary focus:ring-primary h-10 w-full rounded-sm border pr-9 pl-9 text-sm focus:ring-1 focus:outline-hidden"
            />
            <span v-if="searching" class="text-muted-foreground pointer-events-none absolute top-1/2 right-9 -translate-y-1/2">
                <Loader2 class="h-4 w-4 animate-spin" />
            </span>
            <button
                v-if="query"
                type="button"
                class="text-muted-foreground hover:bg-accent hover:text-foreground absolute top-1/2 right-2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded"
                @click="clearQuery"
            >
                <X class="h-4 w-4" />
                <span class="sr-only">Clear search</span>
            </button>
        </div>

        <div v-if="searchError" class="border-destructive/30 bg-destructive-muted text-destructive rounded-sm border px-3 py-2 text-xs">
            {{ searchError }}
        </div>

        <!-- Initial loading state while we wait for the first response for this query -->
        <div v-if="showSearchingPlaceholder" class="flex flex-col gap-2" role="status" aria-live="polite">
            <div class="text-muted-foreground flex items-center gap-2 px-2 text-xs font-semibold tracking-wider uppercase">
                <Loader2 class="h-3 w-3 animate-spin" />
                Searching...
            </div>
            <div v-for="i in 4" :key="i" class="flex flex-col gap-1 rounded px-2 py-2">
                <div class="bg-muted h-3 w-3/4 animate-pulse rounded" />
                <div class="bg-muted/60 h-2 w-1/2 animate-pulse rounded" />
            </div>
        </div>

        <!-- Semantic search results (shown when query has a response) -->
        <nav v-else-if="showSemanticResults" class="flex flex-col gap-1 text-sm">
            <p class="text-muted-foreground mb-1 flex items-center gap-2 px-2 text-xs font-semibold tracking-wider uppercase">
                Best matches
                <Loader2 v-if="searching" class="h-3 w-3 animate-spin" />
            </p>
            <Link
                v-for="result in searchResults ?? []"
                :key="result.article_slug"
                :href="result.url"
                class="group text-foreground hover:bg-accent hover:text-accent-foreground flex flex-col gap-1 rounded px-2 py-2"
            >
                <span class="font-medium">{{ result.title }}</span>
                <span v-if="result.intro" class="text-muted-foreground line-clamp-2 text-xs">
                    {{ result.intro }}
                </span>
                <span v-if="result.matched_heading" class="text-muted-foreground/80 flex items-center gap-1 text-[11px]">
                    <Hash class="h-2.5 w-2.5" />
                    Matched in {{ result.matched_heading }}
                </span>
            </Link>
        </nav>

        <!-- Active query with an empty server response — fall back to the instant client-side filter -->
        <nav v-else-if="hasActiveQuery && !searching" class="flex flex-col gap-6 text-sm">
            <div v-if="filteredTopLevel.length" class="flex flex-col gap-1">
                <p class="text-muted-foreground mb-1 px-2 text-xs font-semibold tracking-wider uppercase">Start here</p>
                <Link
                    v-for="article in filteredTopLevel"
                    :key="article.path"
                    :href="article.url"
                    :class="[
                        'text-foreground hover:bg-accent hover:text-accent-foreground rounded px-2 py-1.5',
                        isActive(article.path) && 'bg-accent text-accent-foreground font-medium',
                    ]"
                >
                    {{ article.title }}
                </Link>
            </div>
            <template v-for="section in sectionOrder" :key="section">
                <div v-if="filteredSections[section].length" class="flex flex-col gap-1">
                    <p class="text-muted-foreground mb-1 px-2 text-xs font-semibold tracking-wider uppercase">
                        {{ sectionLabels[section] }}
                    </p>
                    <Link
                        v-for="article in filteredSections[section]"
                        :key="article.path"
                        :href="article.url"
                        :class="[
                            'text-foreground hover:bg-accent hover:text-accent-foreground rounded px-2 py-1.5',
                            isActive(article.path) && 'bg-accent text-accent-foreground font-medium',
                        ]"
                    >
                        {{ article.title }}
                    </Link>
                </div>
            </template>
            <div
                v-if="
                    !searching &&
                    filteredTopLevel.length === 0 &&
                    filteredSections.agent.length + filteredSections.supplier.length + filteredSections.shared.length === 0
                "
                class="border-border bg-card text-muted-foreground rounded-sm border px-3 py-6 text-center"
            >
                No articles match &ldquo;{{ query }}&rdquo;.
            </div>
        </nav>

        <!-- Full browsable tree when no query -->
        <nav v-else class="flex flex-col gap-6 text-sm">
            <div v-if="index.topLevel.length" class="flex flex-col gap-1">
                <p class="text-muted-foreground mb-1 px-2 text-xs font-semibold tracking-wider uppercase">Start here</p>
                <Link
                    v-for="article in index.topLevel"
                    :key="article.path"
                    :href="article.url"
                    :class="[
                        'text-foreground hover:bg-accent hover:text-accent-foreground rounded px-2 py-1.5',
                        isActive(article.path) && 'bg-accent text-accent-foreground font-medium',
                    ]"
                >
                    {{ article.title }}
                </Link>
            </div>
            <template v-for="section in sectionOrder" :key="section">
                <div v-if="index.sections[section].length" class="flex flex-col gap-1">
                    <p class="text-muted-foreground mb-1 px-2 text-xs font-semibold tracking-wider uppercase">
                        {{ sectionLabels[section] }}
                    </p>
                    <Link
                        v-for="article in index.sections[section]"
                        :key="article.path"
                        :href="article.url"
                        :class="[
                            'text-foreground hover:bg-accent hover:text-accent-foreground rounded px-2 py-1.5',
                            isActive(article.path) && 'bg-accent text-accent-foreground font-medium',
                        ]"
                    >
                        {{ article.title }}
                    </Link>
                </div>
            </template>
        </nav>
    </aside>
</template>
